import { cx } from '@linaria/core';
import { useRef, useMemo, useLayoutEffect, createElement, useId } from 'react';
import { makeEmptyCaption } from '@pm2/content-engine';
import { TranscriptButton } from '@pm2/element-toolbar';
import { Video23Player, AudioComponent } from '@pm2/media-component';
import { resets } from '@ui/css';
import { Tooltip, speechSelectors, Button } from '@ui/kit';
import { CrossIcon } from '@ui/kit/icons';
import { xml2ast, paragraphHtmlParsers, simpleHtmlParsers, printHtmlNodesToText, printAST, makeHtmlNodeToJsxPrinter, textNodeToJsxPrinter, generateJsxKey, allHtmlParsers, printHtmlNodesToJsx } from '@utils/ast';
import { typesetKatex } from '@utils/common';
import { UniversalContextMenu } from '@utils/component-framework';
import { body, closeButton, heading, wrapper, linkButton, audio, contextMenu, imageWrapper, imageFigure, image, imageCaption } from './molecule.hotspot-content.styles.js';
import { jsxs, jsx } from 'react/jsx-runtime';

function HotspotContent(props) {
  const ref = useRef(null);
  const description = useMemo(() => {
    const descriptionAST = xml2ast(`<div class="${body}">${props.hotspot.text}</div>`, {
      ...simpleHtmlParsers,
      ...paragraphHtmlParsers
    }, "text/html");
    if (!printHtmlNodesToText(descriptionAST).trim().length) {
      return undefined;
    }
    return printAST(descriptionAST, {
      text: textNodeToJsxPrinter,
      html: htmlNodeToJsxPrinter
    });
  }, [props.hotspot.text]);
  useLayoutEffect(() => {
    typesetKatex(ref);
  }, [ref]);
  return /*#__PURE__*/jsxs("section", {
    ref: ref,
    id: props.id,
    className: cx(wrapper, speechSelectors.enableSpeech.className),
    children: [props.onClose && /*#__PURE__*/jsx(Tooltip, {
      ariaType: "label",
      placement: "bottom",
      body: "Luk hotspot",
      children: ref => /*#__PURE__*/jsx("button", {
        className: closeButton,
        ref: ref,
        onClick: props.onClose,
        children: /*#__PURE__*/jsx(CrossIcon, {})
      })
    }), /*#__PURE__*/jsx("h3", {
      className: heading,
      children: props.hotspot.title
    }), /*#__PURE__*/jsx(HotspotMedia, {
      ...props
    }), description]
  });
}
function HotspotMedia(props) {
  const id = useId();
  const longDescAst = useMemo(() => xml2ast(props.hotspot.media.photo?.longDesc ?? "", allHtmlParsers, "text/html"), [props.hotspot.media.photo?.longDesc]);
  switch (props.hotspot.media.selectedMedia) {
    case "photo":
      {
        if (!props.hotspot.media.photo) {
          return null;
        }
        return /*#__PURE__*/jsxs("div", {
          className: imageWrapper,
          children: [!!longDescAst.length && /*#__PURE__*/jsx("div", {
            className: contextMenu,
            children: /*#__PURE__*/jsx(UniversalContextMenu, {
              layout: "solid",
              label: "\xC5bn kontekstmenuen",
              children: /*#__PURE__*/jsx(TranscriptButton, {
                nodeType: "image-component",
                transcript: longDescAst
              }, "transcript")
            })
          }), /*#__PURE__*/jsxs("figure", {
            className: imageFigure,
            children: [/*#__PURE__*/jsx("img", {
              className: image,
              src: props.hotspot.media.photo.src,
              alt: props.hotspot.media.photo.alt ?? "",
              "aria-details": props.hotspot.media.photo.longDesc ? `transcript-${id}` : ""
            }), /*#__PURE__*/jsx("figcaption", {
              className: imageCaption,
              children: props.hotspot.media.photo.copyright
            }), props.hotspot.media.photo.longDesc && /*#__PURE__*/jsx("div", {
              className: resets.screenReaderOnlyCls,
              "data-disable-polly": true,
              id: `transcript-${id}`,
              children: printHtmlNodesToJsx(longDescAst)
            })]
          })]
        });
      }
    case "sound":
      {
        const video23Node = props.hotspot.media.sound?.video23;
        const src = video23Node?.src ?? props.hotspot.media.sound?.id;
        if (!src) {
          return null;
        }
        return /*#__PURE__*/jsxs("div", {
          className: audio,
          children: [!!video23Node?.transcript?.length && /*#__PURE__*/jsx(UniversalContextMenu, {
            togglerClassName: contextMenu,
            layout: "solid",
            label: "\xC5bn kontekstmenuen",
            children: /*#__PURE__*/jsx(TranscriptButton, {
              nodeType: "audio-component",
              transcript: video23Node?.transcript
            }, "transcript")
          }), /*#__PURE__*/jsx(AudioComponent, {
            src: src,
            caption: makeEmptyCaption(),
            imageSrc: video23Node?.thumbnail?.src,
            transcript: video23Node?.transcript ?? undefined,
            isScrambled: false
          })]
        });
      }
    case "video":
      if (!props.hotspot.media.video) {
        return null;
      }
      return /*#__PURE__*/jsx(Video23Player, {
        photoId: props.hotspot.media.video.id,
        token: "",
        aspectRatio: "56.25%",
        title: undefined,
        caption: undefined,
        showSubtitlesByDefault: false
      });
    default:
      return null;
  }
}
const htmlNodeToJsxPrinter = makeHtmlNodeToJsxPrinter({
  skipLeadingBRs: true,
  skipTrailingBRs: true,
  handleTag: (tag, data, context) => {
    if (tag !== "a") {
      return undefined;
    }
    return /*#__PURE__*/createElement(Button, {
      ...data.props,
      className: cx(data.props["className"] ? String(data.props["className"]) : undefined, linkButton),
      key: generateJsxKey(context),
      as: "a",
      theme: "primary"
    }, data.children);
  }
}, () => ({
  html: htmlNodeToJsxPrinter,
  text: textNodeToJsxPrinter
}));

export { HotspotContent };
//# sourceMappingURL=molecule.hotspot-content.js.map
